import { useEffect, useMemo, useState } from "react";
import {
  Check,
  CircleAlert,
  Eye,
  Plus,
  RotateCcw,
  type LucideIcon,
} from "lucide-react-native";
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import {
  CardHeader,
  ClinicalDataStatus,
  Dialog,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
  DocumentRow,
  MetaText,
  CardSurface,
  PrimaryTitleText,
  SupportingSubtitleText,
  spacing,
  BackLink,
  ActionBanner,
  TextActionButton,
} from "@louise/moon-ui";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import type { ThinkingOrbState } from "@src/components/ui/ThinkingOrbStatus.types";
import { useApiClient } from "@src/packages/api/louise-api";
import { type Locale, useLocale, useTranslations } from "@src/packages/i18n";
import { semanticColors } from "@src/packages/moon-ui/theme/tokens";
import {
  getDocumentProcessingRunForUploadJobItem,
  projectDocumentProcessingRunToPrefill,
  type DocumentProcessingRunLookup,
} from "@src/packages/documents/ingestion/documentProcessingRun";
import { countDocumentValuesToReview } from "@src/packages/documents/ingestion/documentValues";
import {
  hasVisibleDocumentActivity,
  isDocumentActivityActive,
  isDocumentActivityAttention,
  summarizeDocumentActivity,
  type DocumentProcessingItem,
} from "@src/packages/documents/ingestion/documentProcessingActivity";
import {
  getUploadFailurePresentation,
  isUploadFailureRetryable,
} from "@src/packages/documents/ingestion/uploadFailurePresentation";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type DocumentProcessingActivityControlProps = {
  processingItems: DocumentProcessingItem[];
  updateProcessingItem: (
    id: string,
    update: Partial<DocumentProcessingItem>,
  ) => void;
  dismissProcessingItem: (id: string) => void;
  onAddMoreDocuments: () => Promise<void> | void;
  /** Opens the document whose found values wait for the patient. */
  onReviewDocumentValues?: (patientDocumentId: string | null) => void;
};

const uploadFileIcons = {
  document:
    require("@src/apps/moon/assets/images/upload-file-icons/document-3d.png") as number,
  image:
    require("@src/apps/moon/assets/images/upload-file-icons/image-3d.png") as number,
  generic:
    require("@src/apps/moon/assets/images/upload-file-icons/generic-3d.png") as number,
} as const;

const COLORS = {
  text: semanticColors.text.primary,
  secondary: semanticColors.text.secondary,
  faint: semanticColors.text.tertiary,
  border: semanticColors.border.subtle,
  surface: semanticColors.surface.glass,
  error: semanticColors.status.danger,
} as const;

function fileIconKind(fileName: string): keyof typeof uploadFileIcons {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (
    extension &&
    ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(extension)
  ) {
    return "image";
  }
  if (
    extension &&
    ["pdf", "doc", "docx", "txt", "csv", "xls", "xlsx"].includes(extension)
  ) {
    return "document";
  }
  return "generic";
}

function FileTypeIcon({ fileName }: { fileName: string }) {
  return (
    <Image
      accessibilityLabel={`${fileIconKind(fileName)} file`}
      source={uploadFileIcons[fileIconKind(fileName)]}
      resizeMode="contain"
      style={{ width: 34, height: 34 }}
    />
  );
}

function DocumentActivityControlIcon({
  accessibilityLabel,
  orbState,
}: {
  accessibilityLabel: string;
  orbState: ThinkingOrbState;
}) {
  return (
    <View
      accessibilityLabel={accessibilityLabel}
      style={{
        height: 44,
        position: "relative",
        width: 44,
      }}
    >
      <Image
        accessibilityLabel="Document processing"
        source={uploadFileIcons.document}
        resizeMode="contain"
        style={{ height: 36, width: 36 }}
      />
      <View
        style={{
          alignItems: "center",
          backgroundColor: "rgba(20, 20, 20, 0.92)",
          borderRadius: 12,
          bottom: 0,
          height: 24,
          justifyContent: "center",
          position: "absolute",
          right: 0,
          width: 24,
        }}
      >
        <ThinkingOrbStatus
          accessibilityLabel={accessibilityLabel}
          state={orbState}
          variant="hero"
          size={20}
          theme="dark"
        />
      </View>
    </View>
  );
}

function orbStateForItem(
  item: DocumentProcessingItem,
): ThinkingOrbState | null {
  switch (item.state) {
    case "selecting":
    case "validating":
      return "listening";
    case "uploading":
      return "composing";
    case "uploaded":
    case "saving":
    case "processing":
      return "shaping";
    case "background_processing":
      return "working";
    case "queued":
      return "listening";
    case "review_ready":
    case "needs_review":
    case "partial_success":
      return "solving";
    default:
      return null;
  }
}

function orbLabelForItem(
  item: DocumentProcessingItem,
  t: ReturnType<typeof useTranslations>,
): string {
  const status = (key: string, params?: Record<string, string>) =>
    t(`documents.upload.item_status.${key}`, params ? { params } : undefined);
  switch (item.state) {
    case "selecting":
      return status("selecting");
    case "validating":
      return status("checking");
    case "uploading":
      return item.progress === undefined
        ? status("uploading")
        : status("uploading_percent", { progress: String(item.progress) });
    case "uploaded":
      return status("preparing");
    case "processing":
      return status("reading");
    case "saving":
      return status("saving");
    case "background_processing":
      return status("working_in_background");
    case "queued":
      return status("next_up");
    case "review_ready":
      return status("ready_to_review");
    case "needs_review":
    case "partial_success":
      return status("review_needed");
    default:
      return "";
  }
}

// A finished state waiting on the patient (ready, saved, review needed)
// reads as a plain label in the same 11 px weight as the row's actions; the
// orb is kept for work still in progress.
const REVIEW_STATES = new Set<DocumentProcessingItem["state"]>([
  "review_ready",
  "needs_review",
  "partial_success",
]);

function StatusLabel({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}) {
  return (
    <View
      accessibilityLabel={label}
      style={{
        alignItems: "center",
        flexDirection: "row",
        gap: 5,
        justifyContent: "center",
        minHeight: 30,
        paddingHorizontal: 4,
      }}
    >
      <Icon color={semanticColors.text.secondary} size={12} strokeWidth={2} />
      <Text
        className="font-dm-sans"
        style={{
          color: semanticColors.text.secondary,
          fontSize: 11,
          lineHeight: 14,
          fontWeight: "700",
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function RetryAction({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <TextActionButton
      label={label}
      action="retry_document_upload"
      onPress={onPress}
      tone="destructive"
      size="xs"
      icon={RotateCcw}
      style={{ paddingHorizontal: 4 }}
    />
  );
}

function FailureAction({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <TextActionButton
      label={label}
      action="document_failure_action"
      onPress={onPress}
      tone="destructive"
      size="xs"
      icon={CircleAlert}
      style={{ paddingHorizontal: 4 }}
    />
  );
}

function ActivityItemStatus({
  item,
  onCancel,
  onPress,
  onRetry,
  t,
}: {
  item: DocumentProcessingItem;
  onCancel: () => void;
  onPress: () => void;
  onRetry: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  if (item.failure) {
    const presentation = getUploadFailurePresentation(item.failure);
    return presentation.retryable ? (
      <RetryAction label={t(presentation.actionKey)} onPress={onRetry} />
    ) : (
      <FailureAction
        label={t("documents.upload.failure.details")}
        onPress={onPress}
      />
    );
  }

  if (REVIEW_STATES.has(item.state)) {
    return <StatusLabel icon={Eye} label={orbLabelForItem(item, t)} />;
  }

  const orbState = orbStateForItem(item);
  if (orbState) {
    const canCancel = ["queued", "uploading"].includes(item.state);
    return (
      <ThinkingOrbStatus
        accessibilityLabel={`${orbLabelForItem(item, t)} ${item.name}`}
        label={orbLabelForItem(item, t)}
        onCancel={canCancel ? onCancel : undefined}
        state={orbState}
        size={20}
        theme="light"
      />
    );
  }

  return (
    <StatusLabel
      icon={Check}
      label={t(
        item.state === "source_only"
          ? "documents.upload.item_status.saved"
          : "documents.upload.item_status.ready",
      )}
    />
  );
}

function ActivityItemRow({
  item,
  onCancel,
  onPress,
  onRetry,
  t,
}: {
  item: DocumentProcessingItem;
  onCancel: () => void;
  onPress: () => void;
  onRetry: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const rowDetail = item.failure
    ? t(getUploadFailurePresentation(item.failure).statusKey)
    : item.detail;

  return (
    <DocumentRow
      accessibilityLabel={`${item.name}, ${rowDetail}`}
      leading={<FileTypeIcon fileName={item.name} />}
      onPress={onPress}
      showChevron={true}
      subtitle={rowDetail}
      title={item.name}
      titleNumberOfLines={1}
      subtitleNumberOfLines={1}
      trailing={
        <ActivityItemStatus
          item={item}
          onCancel={onCancel}
          onPress={onPress}
          onRetry={onRetry}
          t={t}
        />
      }
      style={{ paddingVertical: 9 }}
    />
  );
}

function partialFailureSummary(
  items: readonly DocumentProcessingItem[],
  readyCount: number,
  t: ReturnType<typeof useTranslations>,
  locale: Locale,
) {
  const failures = items.flatMap((item) =>
    item.failure ? [item.failure] : [],
  );
  const count = (codes: string[]) =>
    failures.filter((failure) => codes.includes(failure.code)).length;
  const countParams = (value: number) => ({
    fileCount: `${value} ${
      locale === "fr"
        ? value === 1
          ? "fichier"
          : "fichiers"
        : value === 1
          ? "file"
          : "files"
    }`,
  });
  const parts: string[] = [];

  if (readyCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_ready", {
        params: countParams(readyCount),
      }),
    );
  }

  const notUploadedCount = count([
    "unsupported_type",
    "file_too_large",
    "batch_too_many_files",
    "batch_too_large",
    "empty_file",
    "corrupt_file",
    "password_protected",
  ]);
  if (notUploadedCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_not_uploaded", {
        params: countParams(notUploadedCount),
      }),
    );
  }

  const alreadySavedCount = count(["duplicate"]);
  if (alreadySavedCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_already_saved", {
        params: countParams(alreadySavedCount),
      }),
    );
  }

  const notSavedCount = count(["network_stalled", "upload_failed"]);
  if (notSavedCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_not_saved", {
        params: countParams(notSavedCount),
      }),
    );
  }

  const canceledCount = count(["canceled"]);
  if (canceledCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_canceled", {
        params: countParams(canceledCount),
      }),
    );
  }

  const savedNotReadCount = count(["extraction_failed"]);
  if (savedNotReadCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_saved_not_read", {
        params: countParams(savedNotReadCount),
      }),
    );
  }

  const extractedNotSavedCount = count(["persistence_failed"]);
  if (extractedNotSavedCount > 0) {
    parts.push(
      t("documents.upload.failure.partial_extracted_not_saved", {
        params: countParams(extractedNotSavedCount),
      }),
    );
  }

  return [...parts, t("documents.upload.failure.partial_next_step")].join(" ");
}

function reviewFieldsForItem(item: DocumentProcessingItem) {
  if (/appointment/i.test(item.name)) {
    return [
      { label: "Appointment", value: "May 20, 2026", detail: "Clinic visit" },
      { label: "Source", value: item.name, detail: "Page 1" },
      { label: "Destination", value: "Monitoring", detail: "Care plan" },
    ];
  }
  if (/insurance/i.test(item.name)) {
    return [
      { label: "Document", value: "Insurance card", detail: "Front image" },
      { label: "Provider", value: "Louise Clinic", detail: "High confidence" },
      { label: "Source", value: item.name, detail: "Original attached" },
    ];
  }
  return [
    {
      label: "Changes",
      value: "3 medication changes",
      detail: "High confidence",
    },
    { label: "Source", value: item.name, detail: "Pages 1–2" },
    { label: "Destination", value: "Treatment plan", detail: "Care record" },
  ];
}

function claimValue(
  claim: NonNullable<DocumentProcessingRunLookup["run"]>["claims"][number],
  fallback: string,
) {
  if (claim.rawValue.trim()) return claim.rawValue.trim();
  if (claim.rawDate?.trim()) return claim.rawDate.trim();
  const proposal = claim.normalizedValue?.proposal;
  return typeof proposal === "string" && proposal.trim()
    ? proposal.trim()
    : fallback;
}

function isOpaqueUploadName(value: string) {
  const normalized = value.trim();
  return (
    normalized.length > 96 ||
    /users?[-_/\\]/i.test(normalized) ||
    /generated[_-]?images|exec-[0-9a-f]{8,}/i.test(normalized)
  );
}

function documentTypeLabel(
  classificationCode: string | null | undefined,
  t: ReturnType<typeof useTranslations>,
) {
  switch (classificationCode) {
    case "lab_result":
      return t("documents.doc_type.lab");
    case "imaging_result":
      return t("documents.doc_type.imaging");
    case "prescription":
    case "medication":
      return t("documents.doc_type.treatment_plan");
    case "appointment":
      return t("documents.doc_type.appointment_letter");
    case "genetic":
      return t("documents.doc_type.genetic");
    default:
      return t("documents.doc_type.other");
  }
}

function reviewDocumentTitle(
  item: DocumentProcessingItem,
  lookup: DocumentProcessingRunLookup | null,
  t: ReturnType<typeof useTranslations>,
) {
  const run = lookup?.run;
  const extractedTitle = run?.claims.find((claim) =>
    /(?:^|[._ ])(?:title|report[_ ]?name|document[_ ]?name)$/i.test(
      claim.fieldPath,
    ),
  );
  const extractedTitleValue = extractedTitle
    ? claimValue(extractedTitle, "")
    : "";
  if (extractedTitleValue && !isOpaqueUploadName(extractedTitleValue)) {
    return extractedTitleValue;
  }

  const sourceName = item.name.trim().split(/[\\/]/).pop() ?? item.name.trim();
  const sourceTitle = sourceName.replace(/\.[^.]+$/, "");
  if (sourceTitle && !isOpaqueUploadName(sourceTitle)) return sourceTitle;

  return run
    ? documentTypeLabel(run.classificationCode, t)
    : t("documents.processing_review.document_fallback_title");
}

const MAX_REVIEW_SUMMARY_ROWS = 4;

type ReviewSummaryRow = {
  label: string;
  value: string;
};

function nonEmpty(value: string | null | undefined) {
  return value?.trim() ?? "";
}

function reviewSummaryRows(
  run: NonNullable<DocumentProcessingRunLookup["run"]>,
  t: ReturnType<typeof useTranslations>,
): { title: string; rows: ReviewSummaryRow[] } {
  const projected = projectDocumentProcessingRunToPrefill(run, "");
  const medicationRows = (projected.medications ?? []).map((medication) => ({
    label:
      nonEmpty(medication.medicationName) ||
      t("documents.processing_review.summary_unknown_medication"),
    value: [
      medication.instructions,
      [medication.dosage, medication.dosageUnit]
        .filter((value) => nonEmpty(value))
        .join(" "),
      medication.frequency,
      medication.route || medication.deliveryMethod,
      medication.duration ||
        [medication.startDate, medication.endDate]
          .filter((value) => nonEmpty(value))
          .join(" – "),
      medication.timeOfDay,
    ]
      .map(nonEmpty)
      .filter(Boolean)
      .join(" · "),
  }));
  const appointmentRows = (projected.appointments ?? []).map((appointment) => ({
    label:
      nonEmpty(appointment.title) ||
      t("documents.processing_review.summary_unknown_appointment"),
    value: [appointment.date, appointment.time, appointment.location]
      .map(nonEmpty)
      .filter(Boolean)
      .join(" · "),
  }));
  const resultRows = (projected.results ?? []).map((result) => ({
    label:
      nonEmpty(result.marker) ||
      t("documents.processing_review.summary_unknown_result"),
    value: [result.value, result.unit].map(nonEmpty).filter(Boolean).join(" "),
  }));
  const structuredRows = [
    ...medicationRows,
    ...appointmentRows,
    ...resultRows,
  ].filter((row) => row.value.length > 0);

  if (structuredRows.length > 0) {
    const title =
      medicationRows.length > 0 &&
      appointmentRows.length === 0 &&
      resultRows.length === 0
        ? t("documents.processing_review.summary_medication_plan")
        : appointmentRows.length > 0 &&
            medicationRows.length === 0 &&
            resultRows.length === 0
          ? t("documents.processing_review.summary_appointment")
          : resultRows.length > 0 &&
              medicationRows.length === 0 &&
              appointmentRows.length === 0
            ? t("documents.processing_review.summary_key_results")
            : t("documents.processing_review.summary_key_details");
    return {
      title,
      rows: structuredRows.slice(0, MAX_REVIEW_SUMMARY_ROWS),
    };
  }

  const isUsefulFallbackClaim = (fieldPath: string) => {
    if (
      /^(?:document|prescriber|provider|clinic|facility|metadata)\./i.test(
        fieldPath,
      )
    ) {
      return false;
    }
    if (
      run.classificationCode === "prescription" ||
      run.classificationCode === "medication"
    ) {
      return /medication|drug|dose|dosage|strength|frequency|schedule|route|delivery|form|duration|start|end|time|instruction|direction|sig|quantity|refill|pharmacy/i.test(
        fieldPath,
      );
    }
    return true;
  };

  const fallbackClaimLabel = (claim: (typeof run.claims)[number]) => {
    const rawLabel = nonEmpty(claim.rawLabel);
    if (rawLabel && !/[.\[\]]/.test(rawLabel)) return rawLabel;
    const leaf = claim.fieldPath.split(".").pop() ?? claim.fieldPath;
    return leaf
      .replace(/\[\d+\]/g, "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const fallbackRows = run.claims
    .map((claim) => ({
      label: fallbackClaimLabel(claim),
      value: claimValue(claim, ""),
      fieldPath: claim.fieldPath,
    }))
    .filter(
      (row) =>
        row.value.length > 0 &&
        isUsefulFallbackClaim(row.fieldPath) &&
        !/(?:title|document[_ .]?name|file[_ .]?name|identifier|^date$)/i.test(
          row.fieldPath,
        ),
    )
    .map(({ label, value }) => ({ label, value }));

  return {
    title: t("documents.processing_review.summary_key_details"),
    rows: fallbackRows.slice(0, MAX_REVIEW_SUMMARY_ROWS),
  };
}

function canonicalMeaningPanel({
  lookup,
  loading,
  error,
  onRetry,
  t,
}: {
  lookup: DocumentProcessingRunLookup | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  // The failure stays up while a retry loads, so the button shows its progress.
  if (error) {
    return (
      <ClinicalDataStatus
        state="unavailable"
        title={t("documents.processing_review.meaning_unavailable")}
        message={t("clinical_data.unavailable_body")}
        retryLabel={t("clinical_data.retry")}
        retryingLabel={t("clinical_data.retrying")}
        onRetry={onRetry}
        isRetrying={loading}
        testID="document-processing-meaning-unavailable"
      />
    );
  }

  if (loading) {
    return (
      <CardSurface
        accessibilityLabel={t("documents.processing_review.loading_meaning")}
      >
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          {t("documents.processing_review.loading_meaning")}
        </SupportingSubtitleText>
      </CardSurface>
    );
  }

  const run = lookup?.run;
  if (!run) {
    return (
      <CardSurface
        accessibilityLabel={t("documents.processing_review.meaning_not_ready")}
      >
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          {t("documents.processing_review.meaning_not_ready")}
        </SupportingSubtitleText>
      </CardSurface>
    );
  }

  const reviewSummary = reviewSummaryRows(run, t);
  const anomalyCount = run.claims.reduce(
    (count, claim) => count + claim.anomalies.length,
    0,
  );
  const isIncomplete = run.completeness !== "complete";

  return (
    <View
      accessibilityLabel={t("documents.processing_review.summary_title")}
      style={{ gap: spacing.md }}
      testID="document-processing-canonical-meaning"
    >
      <CardSurface>
        <CardHeader
          title={t("documents.processing_review.summary_title")}
          subtitle={t("documents.processing_review.summary_subtitle")}
        />
        <View style={{ gap: spacing.md, marginTop: spacing.lg }}>
          {reviewSummary.rows.length > 0 ? (
            <>
              <MetaText style={{ marginTop: 0 }}>
                {reviewSummary.title}
              </MetaText>
              {reviewSummary.rows.map((row, index) => (
                <View key={`${row.label}-${row.value}-${index}`}>
                  <PrimaryTitleText>{row.label}</PrimaryTitleText>
                  <SupportingSubtitleText style={{ marginTop: spacing.xs }}>
                    {row.value}
                  </SupportingSubtitleText>
                </View>
              ))}
            </>
          ) : (
            <SupportingSubtitleText style={{ marginTop: 0 }}>
              {t("documents.processing_review.summary_no_action")}
            </SupportingSubtitleText>
          )}
          {isIncomplete || anomalyCount > 0 ? (
            <SupportingSubtitleText
              style={{ color: COLORS.error, marginTop: spacing.xs }}
            >
              {[
                isIncomplete
                  ? t("documents.processing_review.summary_incomplete")
                  : "",
                anomalyCount > 0
                  ? t("documents.processing_review.anomaly_count", {
                      params: { count: String(anomalyCount) },
                    })
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </SupportingSubtitleText>
          ) : null}
        </View>
      </CardSurface>
    </View>
  );
}

function ReviewPreview({
  item,
  onBack,
  onDismiss,
  onConfirmReview,
  onAddMoreDocuments,
  onContinue,
  hasPartialSuccess,
  onSave,
  onRetry,
  onReviewValues,
  meaningLookup,
  meaningLoading,
  meaningError,
  onRetryMeaning,
  t,
}: {
  item: DocumentProcessingItem;
  onBack: () => void;
  onDismiss: () => void;
  onConfirmReview: () => void;
  onAddMoreDocuments: () => void;
  onContinue: () => void;
  hasPartialSuccess: boolean;
  onSave: () => void;
  onRetry: () => void;
  onReviewValues?: () => void;
  meaningLookup: DocumentProcessingRunLookup | null;
  meaningLoading: boolean;
  meaningError: string | null;
  onRetryMeaning: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const isFailure = item.failure != null;
  const isRetryableFailure = item.failure
    ? isUploadFailureRetryable(item.failure)
    : false;
  const failurePresentation = item.failure
    ? getUploadFailurePresentation(item.failure)
    : null;
  const choosesAnotherFile =
    failurePresentation?.actionKey ===
    "documents.upload.failure.choose_another";
  const isSourceOnly = item.state === "source_only";
  const isReview =
    item.state === "review_ready" ||
    item.state === "needs_review" ||
    item.state === "partial_success";
  const isAutomaticCompletion = isReview && item.intent === "dump_anything";
  const isDurableReview =
    isReview && item.durable === true && !isAutomaticCompletion;
  const orbState = orbStateForItem(item);
  const reviewTitle = reviewDocumentTitle(item, meaningLookup, t);
  // Found values are only saved once the patient confirms them, from the
  // document itself.
  const valuesToReview =
    isReview && onReviewValues
      ? countDocumentValuesToReview(meaningLookup?.run)
      : 0;

  return (
    <View style={{ gap: spacing.lg }} testID="document-processing-item-detail">
      <BackLink
        label="Document activity"
        accessibilityLabel="Back to document activity"
        action="back_to_document_activity"
        onPress={onBack}
      />

      <View style={{ alignItems: "center", gap: spacing.sm }}>
        {orbState ? (
          <ThinkingOrbStatus
            accessibilityLabel={orbLabelForItem(item, t)}
            state={orbState}
            variant="hero"
            size={64}
            theme="light"
          />
        ) : (
          <FileTypeIcon fileName={item.name} />
        )}
        <DialogHeader
          align="center"
          title={reviewTitle}
          subtitle={
            item.failure
              ? t(getUploadFailurePresentation(item.failure).statusKey)
              : item.detail
          }
          style={{ alignItems: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", marginTop: 0 }}
        />
      </View>

      {isFailure && failurePresentation ? (
        <ActionBanner
          testID="document-processing-failure"
          variant="important"
          title={t(failurePresentation.titleKey)}
          message={t(failurePresentation.descriptionKey)}
        />
      ) : null}

      {isReview && isDurableReview
        ? canonicalMeaningPanel({
            lookup: meaningLookup,
            loading: meaningLoading,
            error: meaningError,
            onRetry: onRetryMeaning,
            t,
          })
        : null}

      {valuesToReview > 0 ? (
        <CardSurface>
          <SupportingSubtitleText style={{ marginTop: 0 }}>
            {t(
              valuesToReview === 1
                ? "documents.processing_toast.values_found_in_document_one"
                : "documents.processing_toast.values_found_in_document_many",
              {
                params: { count: String(valuesToReview), name: reviewTitle },
              },
            )}
          </SupportingSubtitleText>
        </CardSurface>
      ) : isAutomaticCompletion ? (
        <CardSurface>
          <SupportingSubtitleText style={{ marginTop: 0 }}>
            {t("documents.processing_review.processed_automatically")}
          </SupportingSubtitleText>
        </CardSurface>
      ) : null}

      {isReview && !isDurableReview && !isAutomaticCompletion ? (
        <CardSurface>
          {reviewFieldsForItem(item).map((field) => (
            <View
              key={field.label}
              style={{
                borderBottomColor: semanticColors.border.default,
                borderBottomWidth: 1,
                paddingVertical: spacing.md,
              }}
            >
              <MetaText style={{ marginTop: 0 }}>{field.label}</MetaText>
              <View
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginTop: spacing.xs,
                }}
              >
                <PrimaryTitleText>{field.value}</PrimaryTitleText>
                <SupportingSubtitleText style={{ marginTop: 0 }}>
                  {field.detail}
                </SupportingSubtitleText>
              </View>
            </View>
          ))}
        </CardSurface>
      ) : null}

      {isSourceOnly ? (
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          The original file is saved in Documents. There is no structured
          information to review from this upload.
        </SupportingSubtitleText>
      ) : null}

      {isReview && !isDurableReview && !isAutomaticCompletion ? (
        <SupportingSubtitleText style={{ marginTop: 0 }}>
          Review the extracted information before saving it. The original
          document stays attached to this update.
        </SupportingSubtitleText>
      ) : null}

      {isFailure ? (
        <PrimaryButton
          label={t(
            isRetryableFailure
              ? (failurePresentation?.actionKey ??
                  "documents.upload.failure.details")
              : (failurePresentation?.actionKey ??
                  "documents.upload.failure.choose_another"),
          )}
          onPress={
            isRetryableFailure
              ? onRetry
              : choosesAnotherFile
                ? onAddMoreDocuments
                : onDismiss
          }
          leading={
            isRetryableFailure ? (
              <RotateCcw color={semanticColors.text.inverse} size={16} />
            ) : choosesAnotherFile ? (
              <Plus color={semanticColors.text.inverse} size={16} />
            ) : undefined
          }
          fullWidth
        />
      ) : null}
      {isReview && !isDurableReview && !isAutomaticCompletion ? (
        <PrimaryButton label="Save" onPress={onSave} fullWidth />
      ) : null}
      {valuesToReview > 0 && onReviewValues ? (
        <PrimaryButton
          label={t("documents.processing_review.review_values")}
          action="review_document_values"
          onPress={onReviewValues}
          fullWidth
        />
      ) : null}
      {isDurableReview && valuesToReview === 0 ? (
        <PrimaryButton
          label={t("documents.processing_review.mark_as_reviewed")}
          onPress={onConfirmReview}
          fullWidth
        />
      ) : null}
      {isSourceOnly ? (
        <PrimaryButton label="Done" onPress={onDismiss} fullWidth />
      ) : null}
      {isAutomaticCompletion && valuesToReview === 0 ? (
        <PrimaryButton label="Done" onPress={onDismiss} fullWidth />
      ) : null}
      {isFailure && hasPartialSuccess ? (
        <SecondaryButton
          label={t("documents.upload.failure.continue_ready")}
          onPress={onContinue}
        />
      ) : null}
      <SecondaryButton
        label="Add more documents"
        leading={<Plus color={COLORS.secondary} size={16} />}
        onPress={onAddMoreDocuments}
      />
      {!isFailure && !isReview && !isSourceOnly ? (
        <SecondaryButton label="Back to activity" onPress={onBack} />
      ) : null}
    </View>
  );
}

export function DocumentProcessingActivityControl({
  processingItems,
  updateProcessingItem,
  dismissProcessingItem,
  onAddMoreDocuments,
  onReviewDocumentValues,
}: DocumentProcessingActivityControlProps) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { height: windowHeight } = useWindowDimensions();
  const capturePress = usePressCapture("DocumentProcessingActivityControl");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const visibleItems = useMemo(
    () =>
      processingItems.filter(
        (item) => item.state !== "canceled" && item.state !== "complete",
      ),
    [processingItems],
  );
  const activeCount = visibleItems.filter((item) =>
    isDocumentActivityActive(item.state),
  ).length;
  const attentionCount = visibleItems.filter((item) =>
    isDocumentActivityAttention(item.state),
  ).length;
  const hasActivity = hasVisibleDocumentActivity(processingItems);
  const activitySummary = summarizeDocumentActivity(processingItems);
  const selectedItem = selectedItemId
    ? (processingItems.find((item) => item.id === selectedItemId) ?? null)
    : null;

  const { client, patientId } = useApiClient();
  const [meaningLookup, setMeaningLookup] =
    useState<DocumentProcessingRunLookup | null>(null);
  const [meaningLoading, setMeaningLoading] = useState(false);
  const [meaningError, setMeaningError] = useState<string | null>(null);
  const [meaningAttempt, setMeaningAttempt] = useState(0);

  useEffect(() => {
    setMeaningLookup(null);
    setMeaningError(null);
  }, [selectedItem?.durable, selectedItem?.fileItemId, selectedItem?.jobId]);

  useEffect(() => {
    let active = true;

    if (
      !selectedItem?.durable ||
      !selectedItem.jobId ||
      !selectedItem.fileItemId ||
      !patientId
    ) {
      setMeaningLoading(false);
      return () => {
        active = false;
      };
    }

    setMeaningLoading(true);
    void getDocumentProcessingRunForUploadJobItem(
      client,
      patientId,
      selectedItem.jobId,
      selectedItem.fileItemId,
    )
      .then((lookup) => {
        if (active) {
          setMeaningLookup(lookup);
          setMeaningError(null);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setMeaningError(
            error instanceof Error ? error.message : "Request failed",
          );
        }
      })
      .finally(() => {
        if (active) setMeaningLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    client,
    patientId,
    selectedItem?.durable,
    selectedItem?.fileItemId,
    selectedItem?.jobId,
    meaningAttempt,
  ]);

  if (!hasActivity) return null;

  const summary = activitySummary.failureCount
    ? `${activitySummary.failureCount} document${activitySummary.failureCount === 1 ? "" : "s"} need attention`
    : attentionCount
      ? `${attentionCount} document${attentionCount === 1 ? "" : "s"} ready for attention`
      : `${activeCount} document${activeCount === 1 ? "" : "s"} in progress`;
  const orbState: ThinkingOrbState = attentionCount
    ? "solving"
    : activeCount
      ? "shaping"
      : "working";
  const dialogTitle = activitySummary.failureCount
    ? activitySummary.hasPartialSuccess
      ? t("documents.upload.failure.partial_title")
      : t("documents.upload.failure.attention_title")
    : attentionCount
      ? activeCount
        ? "Louise is working on your documents"
        : "Your documents are ready"
      : "Louise is processing your documents";
  const dialogSubtitle = activitySummary.failureCount
    ? activitySummary.hasPartialSuccess
      ? partialFailureSummary(
          processingItems,
          activitySummary.readyCount,
          t,
          locale,
        )
      : t("documents.upload.failure.attention_body")
    : attentionCount
      ? "Open a file when you are ready. You can review, save, retry, or dismiss it from here."
      : "You can keep using Louise. We will keep working and let you know when something is ready to review.";

  const closeDialog = () => {
    setDialogVisible(false);
    setSelectedItemId(null);
  };

  const addMoreDocuments = () => {
    closeDialog();
    void onAddMoreDocuments();
  };

  const dismissSelectedItem = (item: DocumentProcessingItem) => {
    dismissProcessingItem(item.id);
    setSelectedItemId(null);
  };

  const cancelItem = (item: DocumentProcessingItem) => {
    dismissProcessingItem(item.id);
    if (selectedItemId === item.id) setSelectedItemId(null);
  };

  const retryItem = (item: DocumentProcessingItem) => {
    const checkpoint = item.failure?.checkpoint;
    const retryState =
      checkpoint === "upload"
        ? "uploading"
        : checkpoint === "saving"
          ? "saving"
          : "processing";
    const retryDetail =
      checkpoint === "upload"
        ? "Uploading again"
        : checkpoint === "saving"
          ? "Saving the extracted information again"
          : "Reading again from the saved upload";

    updateProcessingItem(item.id, {
      state: retryState,
      detail: retryDetail,
      failure: null,
    });
    setSelectedItemId(null);

    if (checkpoint === "saving") {
      setTimeout(() => {
        updateProcessingItem(item.id, {
          state: "complete",
          detail: "Saved to your records",
          failure: null,
        });
      }, 1800);
      return;
    }

    if (checkpoint === "upload") {
      setTimeout(() => {
        updateProcessingItem(item.id, {
          state: "processing",
          detail: "Reading the uploaded file",
          failure: null,
        });
      }, 900);
    }

    setTimeout(
      () => {
        updateProcessingItem(item.id, {
          state: "review_ready",
          detail: "Ready to review",
          failure: null,
        });
      },
      checkpoint === "upload" ? 2700 : 1800,
    );
  };

  const saveItem = (item: DocumentProcessingItem) => {
    updateProcessingItem(item.id, {
      state: "complete",
      detail: "Saved to your records",
    });
    setSelectedItemId(null);
  };

  const reviewScrollHeight = Math.max(
    320,
    Math.min(Math.round(windowHeight * 0.72), 680),
  );

  return (
    <>
      <Pressable
        accessibilityLabel={`Document activity: ${summary}`}
        accessibilityRole="button"
        accessibilityState={{ expanded: dialogVisible }}
        onPress={() => {
          capturePress(pressLabel(summary), "open_document_activity");
          setDialogVisible(true);
        }}
        testID="document-processing-activity-control"
        style={({ pressed }) => [
          {
            alignItems: "center",
            backgroundColor: "rgba(20, 20, 20, 0.92)",
            borderRadius: 29,
            height: 58,
            justifyContent: "center",
            width: 58,
          },
          pressed ? { opacity: 0.78 } : null,
        ]}
      >
        <DocumentActivityControlIcon
          accessibilityLabel={summary}
          orbState={orbState}
        />
        {attentionCount > 0 ? (
          <View
            style={{
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              borderRadius: 999,
              height: 17,
              justifyContent: "center",
              minWidth: 17,
              paddingHorizontal: 4,
              position: "absolute",
              right: 3,
              top: 3,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ color: "#2A2522", fontSize: 10, fontWeight: "900" }}
            >
              {attentionCount}
            </Text>
          </View>
        ) : null}
      </Pressable>

      <Dialog
        visible={dialogVisible}
        onClose={closeDialog}
        closeButton="minimal"
        animationType="fade"
        surfaceStyle={{ maxHeight: "100%" }}
        contentStyle={{
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: 22,
        }}
        testID="document-processing-dialog"
      >
        <ScrollView
          accessibilityLabel="Scrollable document activity"
          contentContainerStyle={{ paddingBottom: 6 }}
          nestedScrollEnabled
          showsVerticalScrollIndicator={true}
          style={
            selectedItem
              ? { height: reviewScrollHeight }
              : { maxHeight: reviewScrollHeight }
          }
          testID="document-processing-review-scroll"
        >
          {selectedItem ? (
            <ReviewPreview
              item={selectedItem}
              onBack={() => setSelectedItemId(null)}
              onDismiss={() => dismissSelectedItem(selectedItem)}
              onConfirmReview={() => dismissSelectedItem(selectedItem)}
              onAddMoreDocuments={addMoreDocuments}
              onContinue={() => setSelectedItemId(null)}
              hasPartialSuccess={activitySummary.hasPartialSuccess}
              onRetry={() => retryItem(selectedItem)}
              onSave={() => saveItem(selectedItem)}
              onReviewValues={
                onReviewDocumentValues
                  ? () => {
                      const documentId =
                        meaningLookup?.patientDocumentId ?? null;
                      dismissSelectedItem(selectedItem);
                      closeDialog();
                      onReviewDocumentValues(documentId);
                    }
                  : undefined
              }
              meaningLookup={meaningLookup}
              meaningLoading={meaningLoading}
              meaningError={meaningError}
              onRetryMeaning={() => setMeaningAttempt((attempt) => attempt + 1)}
              t={t}
            />
          ) : (
            <View style={{ gap: 18 }}>
              <View style={{ alignItems: "center", gap: 4 }}>
                <ThinkingOrbStatus
                  accessibilityLabel={dialogTitle}
                  state={orbState}
                  variant="hero"
                  size={64}
                  theme="light"
                />
                <DialogHeader
                  align="center"
                  title={dialogTitle}
                  subtitle={dialogSubtitle}
                  style={{ alignItems: "center", width: "100%" }}
                  subtitleStyle={{ textAlign: "center", marginTop: 0 }}
                />
              </View>

              <View style={{ gap: 9 }}>
                {visibleItems.map((item) => (
                  <ActivityItemRow
                    key={item.id}
                    item={item}
                    onCancel={() => cancelItem(item)}
                    onPress={() => setSelectedItemId(item.id)}
                    onRetry={() => retryItem(item)}
                    t={t}
                  />
                ))}
              </View>

              {activitySummary.hasPartialSuccess ? (
                <>
                  <PrimaryButton
                    label={t("documents.upload.failure.continue_ready")}
                    onPress={closeDialog}
                    fullWidth
                  />
                </>
              ) : null}

              <SecondaryButton
                label="Add more documents"
                leading={<Plus color={COLORS.secondary} size={16} />}
                onPress={addMoreDocuments}
              />
            </View>
          )}
        </ScrollView>
      </Dialog>
    </>
  );
}

export default DocumentProcessingActivityControl;
